import { useQuery } from '@apollo/client';
import React, { useCallback, useMemo, useState } from 'react';
import { Loader } from 'semantic-ui-react';
import { SEARCH_TAGS } from '../../../utils/apollo/entitySelection/query';
import {
  FieldContext,
  ISearchTagsQuery,
  ISearchTagsQueryVariables,
  PermissionKey,
  TagOption,
} from '../../../utils/apollo/graphqlSchema';
import {
  IBranchNode,
  IEntityNode,
  ILoadableEntity,
  ISearchTagsState,
  ISelectedEntity,
} from '../../Builder/EntitySelection/types';
import {
  createSearchTagsState,
  createTagOptionInput,
} from '../../Builder/EntitySelection/utils';
import { EntityTabType } from './types';
import * as DictionaryTabsUI from './DictionaryTabsUI';
import { useEntityLoader } from '../../Builder/EntitySelection/useEntityLoader';
import usePermission from '../../../utils/hooks/permissionHooks/usePermission';
import { FavoriteStarIcon } from '../../Icon';
import { assertCondition } from '../../../utils/types';
import DictionaryEntityNode from '../DictionaryEntityNode';
import { getEntityType } from './utils';
import { useUserSettingsSelector } from '../../../redux/selectorHooks';
import QuickAccessDictionaryNoResults from '../QuickAccessDictionaryNoResults';
import { DefaultView } from '../../../utils/userSettings';
import Accordion from '../../Accordion';
import { useDictionarySchemaId } from './useDictionarySchemaId';

interface IProps {
  searchTerm: string;
  entityTabType: EntityTabType;
  activeIndices: number[];
  toggleActiveIndex: (index: number) => void;
}

const DictionaryTab = ({
  searchTerm,
  entityTabType,
  activeIndices,
  toggleActiveIndex,
}: IProps) => {
  const schemaId = useDictionarySchemaId();
  const defaultView = useUserSettingsSelector('defaultView');

  const [searchTagState, setSearchTagState] = useState<ISearchTagsState>();
  const [selectedNode, setSelectedNode] = useState<IEntityNode | null>(null);
  const [selectedEntity, setSelectedEntity] = useState<ISelectedEntity | null>(
    null,
  );

  const { fetchEntity, getEntityFromCacheOrFetch } = useEntityLoader(schemaId);

  const { checkIsPermitted } = usePermission();
  const isPermittedToReadEvent = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.EventRead],
    schemaId,
  });
  const isPermittedToReadVirtualEvent = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.VirtualEventRead],
    schemaId,
  });
  const isPermittedToReadMeasure = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.MeasureRead],
    schemaId,
  });
  const isPermittedToReadField = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.FieldRead],
    schemaId,
  });
  const isPermittedToReadFilter = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.FilterRead],
    schemaId,
  });
  const isPermittedToReadCohort = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.CohortRead],
    schemaId,
  });

  const tags = useMemo(() => {
    if (entityTabType === EntityTabType.Events) {
      const inputs = [];

      if (isPermittedToReadEvent) {
        inputs.push(createTagOptionInput(TagOption.BasicEvent));
      }

      if (isPermittedToReadVirtualEvent) {
        inputs.push(createTagOptionInput(TagOption.VirtualEvent));
      }

      return inputs;
    } else if (
      entityTabType === EntityTabType.Measures &&
      isPermittedToReadMeasure
    ) {
      return [
        createTagOptionInput(TagOption.SimpleMeasure),
        createTagOptionInput(TagOption.CompoundMeasure),
      ];
    } else if (entityTabType === EntityTabType.Fields) {
      const inputs = [];

      if (isPermittedToReadField) {
        inputs.push(
          createTagOptionInput(TagOption.Field, {
            field: {
              events: [],
              func: null,
              context: FieldContext.Filter,
              arrayObjectContext: null,
            },
          }),
        );
      }

      if (isPermittedToReadFilter) {
        inputs.push(createTagOptionInput(TagOption.Filter));
      }

      return inputs;
    } else if (
      entityTabType === EntityTabType.Cohorts &&
      isPermittedToReadCohort
    ) {
      return [
        createTagOptionInput(TagOption.SimpleCohort),
        createTagOptionInput(TagOption.CompoundCohort),
      ];
    }
    return [];
  }, [
    entityTabType,
    isPermittedToReadCohort,
    isPermittedToReadEvent,
    isPermittedToReadField,
    isPermittedToReadFilter,
    isPermittedToReadMeasure,
    isPermittedToReadVirtualEvent,
  ]);

  const { loading, refetch } = useQuery<
    ISearchTagsQuery,
    ISearchTagsQueryVariables
  >(SEARCH_TAGS, {
    variables: {
      schemaId,
      searchTerm,
      tags,
      managedOnly: defaultView === DefaultView.ManagedView,
    },
    skip: !schemaId,
    onCompleted: (data) => {
      setSearchTagState(createSearchTagsState(data.searchTags));
    },
  });

  const onClear = useCallback(() => {
    setSelectedEntity(null);
    setSelectedNode(null);
  }, []);

  const onLoadEntity = (loadedEntity: ILoadableEntity) => {
    assertCondition(
      loadedEntity.__typename === 'BasicEventValue' ||
        loadedEntity.__typename === 'CompoundCohort' ||
        loadedEntity.__typename === 'CompoundMeasure' ||
        loadedEntity.__typename === 'ExpressionCohort' ||
        loadedEntity.__typename === 'Field' ||
        loadedEntity.__typename === 'FunnelCohort' ||
        loadedEntity.__typename === 'MeasureCohort' ||
        loadedEntity.__typename === 'NamedFilter' ||
        loadedEntity.__typename === 'PerformedCohort' ||
        loadedEntity.__typename === 'RetentionCohort' ||
        loadedEntity.__typename === 'SimpleMeasure' ||
        loadedEntity.__typename === 'VirtualEvent',
    );
    setSelectedEntity(loadedEntity);
  };

  const onSelectNode = async (node: IEntityNode) => {
    setSelectedNode(node);

    // loadEntity will check the cache, if entity not in cache fetch latest
    const loadedEntity = await getEntityFromCacheOrFetch(node);

    if (loadedEntity) {
      onLoadEntity(loadedEntity);
    }
  };

  const onRefresh = async () => {
    if (selectedNode) {
      // hard refetch the entity for updated details
      const loadedEntity = await fetchEntity(selectedNode);

      if (loadedEntity) {
        onLoadEntity(loadedEntity);
      }
    }
    refetch();
  };

  const sections: IBranchNode[] = useMemo(() => {
    if (searchTagState) {
      const items = searchTagState.root.children;
      const sections: IBranchNode[] = [];

      items.forEach((item) => {
        if (item.__typename === 'TagBranch' && item.children.length > 0) {
          sections.push(item);
        }
      });

      return sections;
    }

    return [];
  }, [searchTagState]);

  if (loading) {
    return <Loader active />;
  }

  if (sections.length === 0) {
    return (
      <QuickAccessDictionaryNoResults
        entityTabType={entityTabType}
        searchTerm={searchTerm}
      />
    );
  }

  return (
    <DictionaryTabsUI.Container>
      {sections.map((section, index) => {
        return (
          <Accordion
            key={`Entity-Section-${index}`}
            title={
              <>
                <DictionaryTabsUI.Text>{section.name}</DictionaryTabsUI.Text>
                {section.name === 'My Favorites' && (
                  <FavoriteStarIcon isActive={true} />
                )}
              </>
            }
            isDefaultExpanded={activeIndices.includes(index)}
            onToggle={() => toggleActiveIndex(index)}
          >
            {section.children
              .filter((node) => {
                return (
                  node.__typename === 'TagLeafBasicEvent' ||
                  node.__typename === 'TagLeafVirtualEvent' ||
                  node.__typename === 'TagLeafSimpleMeasure' ||
                  node.__typename === 'TagLeafCompoundMeasure' ||
                  node.__typename === 'TagLeafField' ||
                  node.__typename === 'TagLeafFilter' ||
                  node.__typename === 'TagLeafSimpleCohort' ||
                  node.__typename === 'TagLeafCompoundCohort'
                );
              })
              .map((node, nodeIndex) => {
                assertCondition(
                  node.__typename === 'TagLeafBasicEvent' ||
                    node.__typename === 'TagLeafVirtualEvent' ||
                    node.__typename === 'TagLeafSimpleMeasure' ||
                    node.__typename === 'TagLeafCompoundMeasure' ||
                    node.__typename === 'TagLeafField' ||
                    node.__typename === 'TagLeafFilter' ||
                    node.__typename === 'TagLeafSimpleCohort' ||
                    node.__typename === 'TagLeafCompoundCohort',
                );

                const nodeKey = `Entity-Section-${index}-Node-${nodeIndex}`;

                return (
                  <DictionaryEntityNode
                    key={nodeKey}
                    id={nodeKey}
                    type={getEntityType(node)}
                    node={node}
                    schemaId={schemaId}
                    entity={selectedEntity}
                    onOpen={() => onSelectNode(node)}
                    onClose={onClear}
                    onRefresh={onRefresh}
                  />
                );
              })}
          </Accordion>
        );
      })}
    </DictionaryTabsUI.Container>
  );
};

export default DictionaryTab;
